import { ChevronRight } from 'lucide-react';
import type { SaludDeFuente } from '@/api/cartera.api';
import { cn } from '@/lib/cn';

/**
 * LA SALUD DE CADA FUENTE (T14). el dueño, 12-oct: «ver de cada base de datos cuál es su salud,
 * qué está funcionando […] la que sea más potable, se le sigue inyectando».
 *
 * Una fila por fuente, de mejor a peor (el orden lo da el servidor), con lo que pasó con sus
 * contactos y una nota que se explica sola. Tocar una lleva a sus clientes. Sin estado: se prueba
 * con renderToStaticMarkup. Los números los calcula el servidor (`saludDeFuentes`).
 */

/** La regla de la nota, dicha como la aplica el servidor (`notaDeSalud`). */
export const REGLA_DE_LA_NOTA =
  'Buena: avanzó el 30 % o más y contestó la mitad o más. Floja: avanzó menos del 10 % o contestó menos del 25 %. Regular: lo demás. Con menos de 10 contactos no hay nota todavía.';

const NOTAS: Record<NonNullable<SaludDeFuente['nota']>, { texto: string; clase: string }> = {
  buena: { texto: 'Buena', clase: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  regular: { texto: 'Regular', clase: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200' },
  floja: { texto: 'Floja', clase: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
};
const SIN_NOTA = { texto: 'Pocos datos', clase: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' };

const MEDIDAS: ReadonlyArray<{ clave: 'contestaron' | 'avanzaron' | 'zoom' | 'cerrados' | 'perdidos'; etiqueta: string; ayuda: string; mala?: boolean }> = [
  { clave: 'contestaron', etiqueta: 'Contestaron', ayuda: 'Escribieron alguna vez' },
  { clave: 'avanzaron', etiqueta: 'Avanzaron', ayuda: 'Pasaron de la primera etapa' },
  { clave: 'zoom', etiqueta: 'Zoom', ayuda: 'Con Zoom agendado o hecho' },
  { clave: 'cerrados', etiqueta: 'Cerrados', ayuda: 'Ya compraron' },
  { clave: 'perdidos', etiqueta: 'Fríos o perdidos', ayuda: 'Temperatura fría o no cerrado', mala: true },
];

function Medida({ etiqueta, ayuda, valor, mala = false }: { etiqueta: string; ayuda: string; valor: number; mala?: boolean }) {
  return (
    <div title={ayuda} className="min-w-0">
      <div className="flex items-baseline justify-between gap-1">
        <span className="truncate text-xs text-slate-500 dark:text-slate-400">{etiqueta}</span>
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{`${valor} %`}</span>
      </div>
      <div aria-hidden className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className={cn('h-full rounded-full', mala ? 'bg-rose-500 dark:bg-rose-400' : 'bg-brand-600 dark:bg-brand-400')} style={{ width: `${Math.min(100, Math.max(0, valor))}%` }} />
      </div>
    </div>
  );
}

/** Una fuente: su nombre, su nota, cuántos son y cómo les fue. Es un botón: lleva a sus clientes. */
export function TarjetaDeFuente({ salud, onElegir }: { salud: SaludDeFuente; onElegir?: () => void }) {
  const nota = salud.nota ? NOTAS[salud.nota] : SIN_NOTA;
  return (
    <button
      type="button"
      onClick={onElegir}
      aria-label={`${salud.fuente}: ${nota.texto}. ${salud.porque} Ver sus clientes`}
      className={cn(
        'group block w-full rounded-xl border border-surface-border bg-surface p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
        'dark:border-slate-800 dark:hover:border-brand-500/50 dark:hover:bg-brand-900/10',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900 dark:text-slate-100">{salud.fuente}</span>
        <span className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold', nota.clase)}>{nota.texto}</span>
        <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
      </div>
      <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
        <span className="tabular-nums">{`${salud.total} ${salud.total === 1 ? 'contacto' : 'contactos'}`}</span>
        <span className="text-slate-400"> · </span>
        <span className="tabular-nums">{`${salud.ultimos30} en los últimos 30 días`}</span>
      </p>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-3 lg:grid-cols-5 lg:gap-x-6">
        {MEDIDAS.map((m) => (
          <Medida key={m.clave} etiqueta={m.etiqueta} ayuda={m.ayuda} valor={salud[m.clave]} mala={m.mala} />
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{salud.porque}</p>
    </button>
  );
}

/** Todas las fuentes, de mejor a peor, con la regla de la nota arriba. */
export function SaludDeFuentes({ fuentes, onElegir }: { fuentes: SaludDeFuente[]; onElegir: (fuente: string) => void }) {
  return (
    <section aria-labelledby="salud-de-fuentes" className="space-y-3">
      <div className="space-y-1">
        <h2 id="salud-de-fuentes" className="text-base font-semibold text-slate-900 dark:text-slate-100">
          De qué base vienen tus clientes y cuál rinde más
        </h2>
        <p className="max-w-[75ch] text-sm text-slate-600 dark:text-slate-300">{`De mejor a peor. ${REGLA_DE_LA_NOTA} Toca una para ver sus clientes.`}</p>
      </div>
      {fuentes.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Todavía no hay clientes que medir. Cuando importes un Excel o te escriban, aquí verás de dónde vienen.
        </p>
      ) : (
        <ul className="space-y-3">
          {fuentes.map((s) => (
            <li key={s.fuente} className="min-w-0">
              <TarjetaDeFuente salud={s} onElegir={() => onElegir(s.fuente)} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
